import { PageTitle, BackButton } from '@Root/components'
import styles from './styles.module.scss'
import { useParams } from 'react-router-dom'
import { API } from '@Root/API'
import { useEffect, useState, useCallback, useRef } from 'react'
import { useHistory } from 'react-router-dom'

export const RawSubmissionData = () => {
  const { submissionId, id } = useParams()
  const [submissionData, setSubmissionData] = useState(null)
  const [rawData, setRawData] = useState('')
  const [parsedData, setParsedData] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const history = useHistory()
  const rawTextareaRef = useRef(null)
  const parsedTextareaRef = useRef(null)

  const formatJSON = jsonString => {
    try {
      const parsed = JSON.parse(jsonString)
      return JSON.stringify(parsed, null, 2)
    } catch (e) {
      return jsonString
    }
  }

  const minifyJSON = jsonString => {
    try {
      const parsed = JSON.parse(jsonString)
      return JSON.stringify(parsed)
    } catch (e) {
      return jsonString
    }
  }

  const handleFormatRaw = () => {
    if (rawData) {
      const formatted = formatJSON(rawData)
      setRawData(formatted)
    }
  }

  const handleMinifyRaw = () => {
    if (rawData) {
      const minified = minifyJSON(rawData)
      setRawData(minified)
    }
  }

  const handleFormatParsed = () => {
    if (parsedData) {
      const formatted = formatJSON(parsedData)
      setParsedData(formatted)
    }
  }

  const handleMinifyParsed = () => {
    if (parsedData) {
      const minified = minifyJSON(parsedData)
      setParsedData(minified)
    }
  }

  const fetchDataHandler = useCallback(async () => {
    try {
      setIsLoading(true)
      const response = await API.cognitoForms.getRawSubmissionData(submissionId)
      const data = response.data || response

      setSubmissionData(data.data || data)

      if (data?.data?.raw_data) {
        const rawDataString = typeof data?.data?.raw_data === 'string' ? data?.data?.raw_data : JSON.stringify(data?.data?.raw_data, null, 2)
        setRawData(rawDataString)
      }

      if (data?.data?.parsed_data) {
        const parsedDataString = typeof data?.data?.parsed_data === 'string' ? data?.data?.parsed_data : JSON.stringify(data?.data?.parsed_data, null, 2)
        setParsedData(parsedDataString)
      }
    } catch (error) {
      console.error('Error fetching submission data:', error)
    } finally {
      setIsLoading(false)
    }
  }, [submissionId])

  useEffect(() => {
    fetchDataHandler()
  }, [fetchDataHandler])

  const formatDate = dateString => {
    if (!dateString) return '-'
    try {
      const date = new Date(dateString)
      return date.toLocaleString('uk-UA', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
    } catch (e) {
      return dateString
    }
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.titleWrap}>
        <BackButton text={'Submissions'} clickHandler={() => history.push(`/home/forms/${id}/details`)} />
        <PageTitle title={'Raw Submission Data'} />
      </div>

      {submissionData && (
        <>
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Submission Information</h2>
            <div className={styles.infoGrid}>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Submission ID:</span>
                <span className={styles.infoValue}>{submissionData.id || '-'}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Cognito Entry ID:</span>
                <span className={styles.infoValue}>{submissionData.cognito_entry_id || '-'}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Form:</span>
                <span className={styles.infoValue}>{submissionData.form_id || submissionData.form || '-'}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Status:</span>
                <span className={`${styles.infoValue} ${styles.status} ${styles[submissionData.status?.toLowerCase()] || ''}`}>
                  {submissionData.status_label || submissionData.status || '-'}
                </span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Created:</span>
                <span className={styles.infoValue}>{formatDate(submissionData.created_at)}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Updated:</span>
                <span className={styles.infoValue}>{formatDate(submissionData.updated_at)}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Contact:</span>
                <span className={styles.infoValue}>{submissionData.contact_email || submissionData.contact_name || '-'}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Sync Attempts:</span>
                <span className={styles.infoValue}>{submissionData.sync_attempts || 0}</span>
              </div>
              {submissionData.sync_error && (
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Sync Error:</span>
                  <span className={`${styles.infoValue} ${styles.syncError}`}>{submissionData.sync_error}</span>
                </div>
              )}
            </div>
          </div>

          {rawData && (
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <h2 className={styles.sectionTitle}>Raw Data (Original from Cognito)</h2>
                <div className={styles.buttons}>
                  <button className={styles.formatButton} onClick={handleFormatRaw}>
                    Format
                  </button>
                  <button className={styles.formatButton} onClick={handleMinifyRaw}>
                    Minify
                  </button>
                </div>
              </div>
              <div className={styles.editorContainer}>
                <textarea ref={rawTextareaRef} className={styles.jsonEditor} value={rawData} onChange={e => setRawData(e.target.value)} spellCheck={false} />
              </div>
            </div>
          )}

          {parsedData && (
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <h2 className={styles.sectionTitle}>Parsed Data (Flattened)</h2>
                <div className={styles.buttons}>
                  <button className={styles.formatButton} onClick={handleFormatParsed}>
                    Format
                  </button>
                  <button className={styles.formatButton} onClick={handleMinifyParsed}>
                    Minify
                  </button>
                </div>
              </div>
              <div className={styles.editorContainer}>
                <textarea
                  ref={parsedTextareaRef}
                  className={styles.jsonEditor}
                  value={parsedData}
                  onChange={e => setParsedData(e.target.value)}
                  spellCheck={false}
                />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
